var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { property } from 'lit/decorators.js';
import { $markLoaded, $needsRender, $onModelLoad, $progressTracker, $scene, $shouldAttemptPreload, $updateSource, $updateStatus, } from '../model-viewer-base.js';
import { $updateEnvironment } from './environment.js';
import { VideoProjection, } from '../three-components/VideoObject.js';
import { isVideoUrl } from '../utilities/content-type-detector.js';
import { clamp } from '../utilities.js';
const $videoObject = Symbol('videoObject');
const $playButton = Symbol('playButton');
const $onPlayButtonClick = Symbol('onPlayButtonClick');
const $updatePlayButtonVisibility = Symbol('updatePlayButtonVisibility');
const $createPlayButton = Symbol('createPlayButton');
const playIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 6px;">
  <path d="M8 5v14l11-7z"/>
</svg>`;
const pauseIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 6px;">
  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
</svg>`;
/**
 * VideoMixin adds support for loading and displaying videos in model-viewer.
 * Videos are automatically detected by file extension.
 */
export const VideoMixin = (ModelViewerElement) => {
    var _a, _b, _c;
    class VideoModelViewerElement extends ModelViewerElement {
        constructor() {
            super(...arguments);
            this.videoProjection = VideoProjection.Flat;
            this[_a] = null;
            this[_b] = null;
            this[_c] = async () => {
                const videoObject = this[$videoObject];
                if (!videoObject)
                    return;
                if (videoObject.paused) {
                    // Unmute on play for user interaction
                    videoObject.muted = false;
                    await videoObject.play();
                }
                else {
                    videoObject.pause();
                }
                this[$updatePlayButtonVisibility]();
                this[$needsRender]();
            };
        }
        connectedCallback() {
            super.connectedCallback();
            this[$updatePlayButtonVisibility]();
        }
        disconnectedCallback() {
            super.disconnectedCallback();
            if (this[$playButton]) {
                this[$playButton].remove();
                this[$playButton] = null;
            }
        }
        updated(changedProperties) {
            super.updated(changedProperties);
            // Update video properties if they changed
            if (this[$videoObject] &&
                changedProperties.has('videoProjection')) {
                this[$videoObject].projection = this.videoProjection;
                this[$needsRender]();
            }
        }
        [(_a = $videoObject, _b = $playButton, _c = $onPlayButtonClick, $updatePlayButtonVisibility)]() {
            const videoObject = this[$videoObject];
            if (!videoObject) {
                if (this[$playButton]) {
                    this[$playButton].style.display = 'none';
                }
                return;
            }
            if (!this[$playButton]) {
                this[$createPlayButton]();
            }
            const button = this[$playButton];
            button.style.display = 'flex';
            const isPlaying = !videoObject.paused;
            button.innerHTML = isPlaying ? `${pauseIcon}Pause` : `${playIcon}Play`;
            button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        }
        [$createPlayButton]() {
            const button = document.createElement('button');
            button.type = 'button';
            button.setAttribute('part', 'video-play-button');
            button.setAttribute('aria-label', 'Play video');
            // Style the button - rectangular, bottom center
            Object.assign(button.style, {
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '8px 20px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: '10',
                transition: 'background-color 0.2s, transform 0.2s',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                fontSize: '14px',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontWeight: '500',
            });
            button.innerHTML = `${playIcon}Play`;
            // Hover effect
            button.addEventListener('mouseenter', () => {
                button.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
                button.style.transform = 'translateX(-50%) scale(1.05)';
            });
            button.addEventListener('mouseleave', () => {
                button.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                button.style.transform = 'translateX(-50%) scale(1)';
            });
            button.addEventListener('click', this[$onPlayButtonClick]);
            // Add to shadow root
            const shadowRoot = this.shadowRoot;
            if (shadowRoot) {
                const container = shadowRoot.querySelector('.container');
                if (container) {
                    container.appendChild(button);
                }
            }
            this[$playButton] = button;
        }
        async [$updateSource]() {
            var _d;
            const scene = this[$scene];
            const source = this.src;
            // Check if the source is a video
            const isVideo = source ? isVideoUrl(source) : false;
            if (isVideo) {
                // Handle video loading
                if (this.loaded || !this[$shouldAttemptPreload]() ||
                    source === scene.url) {
                    return;
                }
                if (this.generateSchema) {
                    scene.updateSchema(source);
                }
                this[$updateStatus]('Loading');
                const updateSourceProgress = this[$progressTracker].beginActivity('video-load');
                try {
                    const srcUpdated = scene.setVideoSource(source, (progress) => updateSourceProgress(clamp(progress, 0, 1) * 0.95));
                    const envUpdated = this[$updateEnvironment]();
                    await Promise.all([srcUpdated, envUpdated]);
                    this[$videoObject] = (_d = scene.videoObject) !== null && _d !== void 0 ? _d : null;
                    this[$markLoaded]();
                    this[$onModelLoad]();
                    // Show play button
                    this[$updatePlayButtonVisibility]();
                    this.updateComplete.then(() => {
                        this.dispatchEvent(new CustomEvent('before-render'));
                    });
                    // Wait for shaders to compile and pixels to be drawn.
                    await new Promise((resolve) => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                this.dispatchEvent(new CustomEvent('load', { detail: { url: source } }));
                                resolve();
                            });
                        });
                    });
                }
                catch (error) {
                    this[$videoObject] = null;
                    this[$updatePlayButtonVisibility]();
                    this.dispatchEvent(new CustomEvent('error', {
                        detail: { type: 'loadfailure', sourceError: error },
                    }));
                }
                finally {
                    updateSourceProgress(1.0);
                }
            }
            else {
                // Fall back to default model loading
                this[$videoObject] = null;
                this[$updatePlayButtonVisibility]();
                return super[$updateSource]();
            }
        }
    }
    __decorate([
        property({ type: String, attribute: 'video-projection' })
    ], VideoModelViewerElement.prototype, "videoProjection", void 0);
    return VideoModelViewerElement;
};
//# sourceMappingURL=video.js.map